import { ApiError, requestJson, type FetchLike } from './http';
import type {
  AppConfig,
  DataTokenResponse,
  WorldTokenResponse,
  InviteRequest,
  MeResponse,
  PasswordSignInRequest,
  SessionCodeRequest,
  SetupRequest,
} from './types';

export interface AdminState {
  /** No administrator can sign in yet: the card offers the setup first. */
  setup: boolean;
  /** This deployment can use passkeys. */
  passkeys: boolean;
}

export interface PasskeySetupRequest {
  code?: string;
  link?: string;
  email: string;
  name: string;
}

/** Same-origin control plane. Session is a cookie; no token handling here. */
export function createAppApi(fetchFn: FetchLike = (i, init) => fetch(i, init)) {
  return {
    /** null when signed out (401). */
    async getMe(): Promise<MeResponse | null> {
      try {
        return await requestJson<MeResponse>(fetchFn, '/v1/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    setup(body: SetupRequest): Promise<unknown> {
      return requestJson(fetchFn, '/v1/auth/setup', { method: 'POST', body: JSON.stringify(body) });
    },
    /** An administrator's username, password and authenticator code. */
    passwordSignIn(body: PasswordSignInRequest): Promise<unknown> {
      return requestJson(fetchFn, '/v1/auth/password', { method: 'POST', body: JSON.stringify(body) });
    },
    /** What the sign-in page's administrator part offers: the setup first, and whether passkeys work here. */
    adminState(): Promise<AdminState> {
      return requestJson<AdminState>(fetchFn, '/v1/auth/admin');
    },
    /** The setup code or a setup link, an email and a name: the new passkey's options. */
    passkeySetupOptions(body: PasskeySetupRequest): Promise<Record<string, unknown>> {
      return requestJson(fetchFn, '/v1/auth/passkey/setup/options', { method: 'POST', body: JSON.stringify(body) });
    },
    /** The new passkey: the first admin is created, or the existing admin let in; signed in. */
    passkeySetup(credential: Record<string, unknown>): Promise<unknown> {
      return requestJson(fetchFn, '/v1/auth/passkey/setup', { method: 'POST', body: JSON.stringify({ credential }) });
    },
    passkeyOptions(): Promise<Record<string, unknown>> {
      return requestJson(fetchFn, '/v1/auth/passkey/options', { method: 'POST' });
    },
    /** Sign in with a passkey (an administrator's). */
    passkeySignIn(credential: Record<string, unknown>): Promise<unknown> {
      return requestJson(fetchFn, '/v1/auth/passkey', { method: 'POST', body: JSON.stringify({ credential }) });
    },
    /** Sign in with an invite code (pack `signIn.codes`). */
    inviteSignIn(code: string): Promise<unknown> {
      return requestJson(fetchFn, '/v1/auth/invite', { method: 'POST', body: JSON.stringify({ code }) });
    },
    /** Sign in with a room's session code. */
    sessionSignIn(body: SessionCodeRequest): Promise<unknown> {
      return requestJson(fetchFn, '/v1/auth/session', { method: 'POST', body: JSON.stringify(body) });
    },
    requestInvite(body: InviteRequest): Promise<unknown> {
      return requestJson(fetchFn, '/v1/invite-requests', { method: 'POST', body: JSON.stringify(body) });
    },
    logout(): Promise<unknown> {
      return requestJson(fetchFn, '/v1/auth/logout', { method: 'POST' });
    },
    getDataToken(): Promise<DataTokenResponse> {
      return requestJson<DataTokenResponse>(fetchFn, '/v1/data-token', { method: 'POST' });
    },
    getWorldToken(): Promise<WorldTokenResponse> {
      return requestJson<WorldTokenResponse>(fetchFn, '/v1/world-token', { method: 'POST' });
    },
    getConfig(): Promise<AppConfig> {
      return requestJson<AppConfig>(fetchFn, '/v1/config');
    },
  };
}

export type AppApi = ReturnType<typeof createAppApi>;

export const ENTRA_START_URL = '/v1/auth/entra/start';

export const appApi = createAppApi();
